/**
 * Gold Loan Calculator Service
 * Migrated from 3in1cms legacy/glCalculatorApi.ts
 */

import { request } from "../core/request";
import { API_RESPONSE_CODES, OPERATION_IDS, WEB_SOURCE_IDENTIFIER_GL } from "./constants";
import { createCacheAsync } from "@repo/shared-utils/cache";
import { logErrorToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import type { IGoldRateRequest, IGoldRateResponse, IKaratWiseRate } from "../types/glTypes";

const NEW_GL_API_CONSTANTS = {
  GOLD_RATE: "goldRate",
  KARAT_WISE_RATES: "karatWiseRates",
  GOLD_RATE_EFFECTIVE_DATE: "goldRateEffectiveDate",
  EXPIRY_TIME: 24 * 60 * 60 * 1000, // 24 hours
};

/**
 * Get cookie value
 * @param name - Cookie name
 * @returns Cookie value or empty string
 */
function getCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || "";
  return "";
}

/**
 * Set cookie value
 * @param name - Cookie name
 * @param expiryTime - Expiry time in milliseconds
 * @param value - Cookie value
 */
function setCookie(name: string, expiryTime: number, value: string): void {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + expiryTime).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
}

/**
 * Store gold rate in cookie and localStorage
 * @param rate - Gold rate value
 */
function storeGoldRate(rate: number) {
  try {
    setCookie(NEW_GL_API_CONSTANTS.GOLD_RATE, NEW_GL_API_CONSTANTS.EXPIRY_TIME, rate.toString());
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(NEW_GL_API_CONSTANTS.GOLD_RATE, rate.toString());
    }
  } catch (error) {
    logErrorToNewRelic(error as Error, {
      component: "storeGoldRate",
      action: "setCookie/localStorage.setItem",
    });
  }
}

/**
 * Store karat-wise rates and effective date in cookies
 * @param rates - Karat-wise rates array
 * @param effectiveDate - Effective date
 */
function storeGoldRateData(rates: IKaratWiseRate[], effectiveDate: string) {
  try {
    setCookie(
      NEW_GL_API_CONSTANTS.KARAT_WISE_RATES,
      NEW_GL_API_CONSTANTS.EXPIRY_TIME,
      JSON.stringify(rates)
    );
    setCookie(
      NEW_GL_API_CONSTANTS.GOLD_RATE_EFFECTIVE_DATE,
      NEW_GL_API_CONSTANTS.EXPIRY_TIME,
      effectiveDate
    );
  } catch (error) {
    logErrorToNewRelic(error as Error, {
      component: "storeGoldRateData",
      action: "setCookie",
    });
  }
}

/**
 * Get stored karat-wise rates from cookie
 * @returns Array of karat-wise rates
 */
export function getStoredKaratWiseRates(): IKaratWiseRate[] {
  try {
    const stored = getCookie(NEW_GL_API_CONSTANTS.KARAT_WISE_RATES);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    logErrorToNewRelic(error as Error, {
      component: "getStoredKaratWiseRates",
      action: "getCookie",
    });
    return [];
  }
}

/**
 * Get stored effective date from cookie
 * @returns Effective date or current date
 */
function getStoredEffectiveDate(): string {
  try {
    return getCookie(NEW_GL_API_CONSTANTS.GOLD_RATE_EFFECTIVE_DATE) || new Date().toISOString();
  } catch (error) {
    logErrorToNewRelic(error as Error, {
      component: "getStoredEffectiveDate",
      action: "getCookie",
    });
    return new Date().toISOString();
  }
}

/**
 * Get stored gold rate from cookie or localStorage
 * @returns Gold rate or 0
 */
export function getStoredGoldRate(): number {
  try {
    const fromCookie = +getCookie(NEW_GL_API_CONSTANTS.GOLD_RATE);
    if (fromCookie) return fromCookie;

    if (typeof localStorage !== "undefined") {
      const fromLocal = Number(localStorage.getItem(NEW_GL_API_CONSTANTS.GOLD_RATE));
      return fromLocal || 0;
    }
    return 0;
  } catch (error) {
    logErrorToNewRelic(error as Error, {
      component: "getStoredGoldRate",
      action: "getCookie/localStorage.getItem",
    });
    return 0;
  }
}

/**
 * Fetch gold rate from API
 * @returns Gold rate response or null
 */
export const fetchGoldRate = async (): Promise<IGoldRateResponse | null> => {
  // First check localStorage cache to avoid unnecessary API calls
  const cachedRates = getStoredKaratWiseRates();
  const cachedBaseRate = getStoredGoldRate();
  const cachedEffectiveDate = getStoredEffectiveDate();

  if (cachedRates.length > 0 && cachedBaseRate > 0) {
    return {
      rateResponse: {
        data: {
          dailyRate: cachedBaseRate.toString(),
          effectiveDate: cachedEffectiveDate,
        },
      },
      karatWiseRate: cachedRates,
      description: "Successfully fetched from cache",
      statusCode: API_RESPONSE_CODES.STATUS_CODE_7001,
    };
  }

  try {
    const response = (await request<IGoldRateResponse, IGoldRateRequest>({
      operationId: OPERATION_IDS.goldRate,
      requestData: {
        sourceSysId: WEB_SOURCE_IDENTIFIER_GL,
      },
    })) as IGoldRateResponse;

    if (response?.statusCode === API_RESPONSE_CODES.STATUS_CODE_7001) {
      const dailyGoldRate = Number(response.rateResponse?.data?.dailyRate) || 0;
      if (dailyGoldRate > 0) {
        storeGoldRate(dailyGoldRate);
      }
      const karatWiseRates = response.karatWiseRate;
      const effectiveDate = response.rateResponse?.data?.effectiveDate;
      if (karatWiseRates && karatWiseRates.length > 0 && effectiveDate) {
        storeGoldRateData(karatWiseRates, effectiveDate);
      }
      return response;
    }
    // Log non-successful status code for debugging
    logErrorToNewRelic(
      new Error(`Gold rate API returned non-successful status code: ${response?.statusCode}`),
      {
        operationId: OPERATION_IDS.goldRate,
        component: "fetchGoldRate",
        statusCode: response?.statusCode,
        description: response?.description,
      }
    );
    return null;
  } catch (error) {
    logErrorToNewRelic(error as Error, {
      operationId: OPERATION_IDS.goldRate,
      component: "fetchGoldRate",
    });
    return null;
  }
};

/**
 * Get current gold rate with caching
 * @returns Gold rate response or null
 */
export const CurrentGoldRate = async (): Promise<IGoldRateResponse | null> => {
  try {
    // Changed cache key to avoid conflict with old API cache
    return await createCacheAsync("newGoldRateApiV2", fetchGoldRate);
  } catch (error) {
    logErrorToNewRelic(error as Error, {
      component: "CurrentGoldRate",
      action: "createCacheAsync",
    });
    return null;
  }
};
